import React, { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config';
import { useAgencyContext, useAuth, useTheme } from '../contexts/AppContexts';
import MapErrorBoundary from './MapErrorBoundary';
import ICS_ICONS from '../constants/ICSIcons';
import { AlertCircle, Circle as CircleIcon, Cloud, MapPin, Monitor, Upload, X } from 'lucide-react';
import { MapContainer as LeafletMapContainer, TileLayer, Marker, Popup, Polygon, Polyline, Rectangle, Circle } from 'react-leaflet';
import L from 'leaflet';
import secureStorage from '../utils/secureStorage';
import useImageDrop from '../utils/useImageDrop';

const ICSDashboardOverview = ({ exerciseId }) => {
  const { theme } = useTheme();
  const { selectedAgency } = useAgencyContext();
  const { user } = useAuth();
  const isAdmin = user?.role === 'Super Admin' || user?.role === 'Agency Admin';
  const [weatherImage, setWeatherImage] = useState('');
  const [weatherLocation, setWeatherLocation] = useState({ city: '', province: '' });
  const [customWeather, setCustomWeather] = useState(null); // { path, uploaded_at, uploaded_by, original_name, blobUrl }
  const [customWeatherLoading, setCustomWeatherLoading] = useState(false);
  const [customWeatherError, setCustomWeatherError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [mapObjects, setMapObjects] = useState([]);
  const [exerciseData, setExerciseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weatherLastUpdated, setWeatherLastUpdated] = useState(null);
  const [errors, setErrors] = useState({
    weather: '',
    map: ''
  });
  const mapRef = useRef(null);

  // Fix Leaflet marker icons
  useEffect(() => {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    });
  }, []);

  useEffect(() => {
    if (exerciseId) {
      fetchAllData();
    } else {
      // No exercise selected, set loading to false to show the interface
      setLoading(false);
    }
  }, [exerciseId, selectedAgency]); // Re-fetch when agency context changes

  // Auto-refresh the Weather card every 2 hours so the RSS-driven forecast
  // stays reasonably current without hammering the upstream feed. The
  // interval is silent (no spinner) and only refreshes the weather block —
  // the map/exercise data do not need to be re-pulled on this cadence.
  useEffect(() => {
    if (!exerciseId) return;
    const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
    const id = setInterval(() => {
      refreshWeatherOnly();
    }, TWO_HOURS_MS);
    return () => clearInterval(id);
  }, [exerciseId, selectedAgency]);

  const refreshWeatherOnly = async () => {
    try {
      const token = secureStorage.getToken();
      if (!token) return;
      const preferenceResponse = await fetch(`${API_BASE_URL}/api/users/weather-preference`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!preferenceResponse.ok) return;
      const preferenceData = await preferenceResponse.json();
      if (preferenceData.has_preference) {
        setWeatherImage(preferenceData.rss_feed);
        setWeatherLocation({ city: preferenceData.city, province: preferenceData.province });
        setErrors(prev => ({ ...prev, weather: '' }));
        setWeatherLastUpdated(new Date());
      }
    } catch (err) {
      console.warn('Weather auto-refresh failed:', err?.message || err);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setErrors({
      weather: '',
      map: ''
    });

    // Fetch weather location - check user's saved preference first
    try {
      const token = secureStorage.getToken();
      
      if (token) {
        // Try to fetch user's saved weather preference
        const preferenceResponse = await fetch(`${API_BASE_URL}/api/users/weather-preference`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (preferenceResponse.ok) {
          const preferenceData = await preferenceResponse.json();
          
          if (preferenceData.has_preference) {
            // User has saved preference, use it
            setWeatherImage(preferenceData.rss_feed);
            setWeatherLocation({
              city: preferenceData.city,
              province: preferenceData.province
            });
          } else {
            // User has no saved preference, show empty state
            setErrors(prev => ({ ...prev, weather: 'No weather location set. Click here to select a location.' }));
          }
        } else {
          // Failed to fetch preference
          setErrors(prev => ({ ...prev, weather: 'Failed to load weather preference' }));
        }
      } else {
        // No token, user not logged in
        setErrors(prev => ({ ...prev, weather: 'Please log in to view weather' }));
      }
    } catch (error) {
      console.error('Error fetching weather preference:', error);
      setErrors(prev => ({ ...prev, weather: 'Error connecting to server' }));
    }

    // Fetch map objects for the Leaflet map
    try {
      // Build URL with agency filter if Super Admin has selected an agency
      let mapUrl = `${API_BASE_URL}/api/map-objects?exercise_id=${exerciseId}`;
      if (selectedAgency) {
        mapUrl += `&organization_id=${selectedAgency.id}`;
      }
      
      const mapResponse = await fetch(mapUrl);
      if (mapResponse.ok) {
        const mapData = await mapResponse.json();
        setMapObjects(mapData);
      } else {
        setErrors(prev => ({ ...prev, map: 'Failed to load map data' }));
      }
    } catch (error) {
      console.error('Error fetching map objects:', error);
      setErrors(prev => ({ ...prev, map: 'Error connecting to server' }));
    }

    // Fetch exercise data for map center coordinates + custom weather image metadata
    try {
      const token = secureStorage.getToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const exerciseResponse = await fetch(`${API_BASE_URL}/api/exercise-builder/${exerciseId}`, { headers });
      if (exerciseResponse.ok) {
        const exData = await exerciseResponse.json();
        setExerciseData(exData);
        if (exData?.custom_weather_image_path) {
          await loadCustomWeatherBlob(exData);
        } else {
          setCustomWeather(null);
        }
      }
    } catch (error) {
      console.error('Error fetching exercise data:', error);
    }

    setLoading(false);
    setWeatherLastUpdated(new Date());
  };

  // Revoke any lingering blob URL when the component unmounts or image changes
  useEffect(() => {
    return () => {
      if (customWeather?.blobUrl) URL.revokeObjectURL(customWeather.blobUrl);
    };
  }, [customWeather?.blobUrl]);

  const loadCustomWeatherBlob = async (exData) => {
    if (!exerciseId) return;
    setCustomWeatherLoading(true);
    setCustomWeatherError('');
    try {
      const token = secureStorage.getToken();
      const resp = await fetch(`${API_BASE_URL}/api/exercise-builder/${exerciseId}/weather-image`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        throw new Error(`Failed to load weather image (${resp.status})`);
      }
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      setCustomWeather({
        path: exData.custom_weather_image_path,
        uploaded_at: exData.custom_weather_image_uploaded_at,
        uploaded_by: exData.custom_weather_image_uploaded_by,
        original_name: exData.custom_weather_image_original_name,
        blobUrl,
      });
    } catch (err) {
      console.error('Error loading custom weather image:', err);
      setCustomWeatherError('Failed to load custom weather image');
      setCustomWeather(null);
    } finally {
      setCustomWeatherLoading(false);
    }
  };

  const uploadCustomWeatherImage = async (file) => {
    if (!file || !exerciseId) return;
    if (!file.type?.startsWith('image/')) {
      alert('Please choose a valid image file (JPG, PNG, GIF, WebP).');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      alert('Image exceeds 8 MB limit.');
      return;
    }
    setUploading(true);
    setCustomWeatherError('');
    try {
      const token = secureStorage.getToken();
      const form = new FormData();
      form.append('file', file);
      const resp = await fetch(`${API_BASE_URL}/api/exercise-builder/${exerciseId}/weather-image`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: form,
      });
      if (resp.status === 423) {
        alert('This exercise is locked. Unlock it before uploading a custom weather image.');
        return;
      }
      if (!resp.ok) {
        const detail = await resp.json().catch(() => ({}));
        throw new Error(detail?.detail || `Upload failed (${resp.status})`);
      }
      const meta = await resp.json();
      // Immediately refresh the blob so the new image shows up
      await loadCustomWeatherBlob({
        custom_weather_image_path: meta.custom_weather_image_path,
        custom_weather_image_uploaded_at: meta.custom_weather_image_uploaded_at,
        custom_weather_image_uploaded_by: meta.custom_weather_image_uploaded_by,
        custom_weather_image_original_name: meta.custom_weather_image_original_name,
      });
    } catch (err) {
      console.error('Error uploading custom weather image:', err);
      setCustomWeatherError(err.message || 'Upload failed');
      alert(`Failed to upload custom weather image: ${err.message || 'Unknown error'}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearCustomWeatherImage = async () => {
    if (!exerciseId) return;
    if (!window.confirm('Remove the custom weather image and restore the default weather widget?')) return;
    try {
      const token = secureStorage.getToken();
      const resp = await fetch(`${API_BASE_URL}/api/exercise-builder/${exerciseId}/weather-image`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (resp.status === 423) {
        alert('This exercise is locked. Unlock it before removing the custom weather image.');
        return;
      }
      if (!resp.ok) {
        const detail = await resp.json().catch(() => ({}));
        throw new Error(detail?.detail || `Delete failed (${resp.status})`);
      }
      if (customWeather?.blobUrl) URL.revokeObjectURL(customWeather.blobUrl);
      setCustomWeather(null);
      setCustomWeatherError('');
    } catch (err) {
      console.error('Error clearing custom weather image:', err);
      alert(`Failed to clear custom weather image: ${err.message || 'Unknown error'}`);
    }
  };

  const { dropProps, isDragging } = useImageDrop({
    onFile: uploadCustomWeatherImage,
    accept: ['image/'],
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${theme.colors.primary} mx-auto mb-4`}></div>
          <p className={`${theme.colors.textMuted}`}>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Get map center coordinates
  const mapCenter = exerciseData?.latitude && exerciseData?.longitude 
    ? [exerciseData.latitude, exerciseData.longitude] 
    : [45.0, -93.0]; // Default center

  return (
    <div className="p-6 h-full flex flex-col">
      <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-6`}>ICS Dashboard Overview</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1" style={{ minHeight: '500px', alignItems: 'stretch' }}>
        {/* Left Column: Weather Image Card */}
        <div 
          {...(isAdmin ? dropProps : {})}
          className={`${theme.colors.secondary} rounded-lg p-6 border ${theme.colors.border} flex flex-col transition-all duration-200 group ${isDragging ? 'border-orange-500 ring-2 ring-orange-500 ring-opacity-60' : ''}`}
          style={{ minHeight: '500px' }}
          data-testid="weather-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Cloud className={`h-5 w-5 text-blue-500 mr-2`} />
              <h3 className={`text-lg font-semibold ${theme.colors.textPrimary}`}>Weather Conditions</h3>
              {customWeather && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded bg-orange-600 text-white" title={customWeather.uploaded_by ? `Uploaded by ${customWeather.uploaded_by}` : 'Custom GFA image'}>
                  Custom GFA
                </span>
              )}
            </div>
            {!customWeather && weatherLocation.city && (
              <div className="flex-1 text-center">
                <p className={`text-sm ${theme.colors.textSecondary}`}>
                  <span className="font-semibold">Location:</span> {weatherLocation.city}, {weatherLocation.province}
                </p>
                <p className={`text-xs ${theme.colors.textMuted}`}>
                  Last updated: {new Date().toLocaleString()}
                </p>
              </div>
            )}
            <div className="flex items-center gap-2">
              {isAdmin && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadCustomWeatherImage(f);
                    }}
                    data-testid="weather-upload-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center gap-1 disabled:opacity-50"
                    title={customWeather ? 'Replace custom GFA image' : 'Upload custom GFA image'}
                    data-testid="weather-upload-btn"
                  >
                    <Upload className="h-3 w-3" />
                    {uploading ? 'Uploading…' : (customWeather ? 'Replace' : 'Upload GFA')}
                  </button>
                  {customWeather && (
                    <button
                      type="button"
                      onClick={clearCustomWeatherImage}
                      className="px-2 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center gap-1"
                      title="Clear custom weather image"
                      data-testid="weather-clear-btn"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </>
              )}
              {!customWeather && (
                <button
                  type="button"
                  onClick={() => {
                    const weatherUrl = `${window.location.origin}${window.location.pathname}#ics?exercise=${exerciseId}&menu=weather`;
                    window.location.href = weatherUrl;
                  }}
                  className="px-3 py-1 bg-slate-600 text-white text-sm rounded hover:bg-slate-700 flex items-center gap-1"
                  title="Change weather location"
                  data-testid="weather-location-btn"
                >
                  <MapPin className="h-3 w-3" />
                  Location
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const url = exerciseId ? `#display-weather?exercise=${exerciseId}` : '#display-weather';
                  window.open(url, '_blank');
                }}
                className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 flex items-center gap-1"
                title="Open in Display Mode"
              >
                <Monitor className="h-3 w-3" />
                Display Mode
              </button>
            </div>
          </div>
          
          {customWeather ? (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 bg-gray-900 rounded-lg overflow-hidden flex items-center justify-center min-h-0 relative" style={{ maxHeight: 'calc(100% - 10px)' }}>
                {customWeatherLoading ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                ) : (
                  <img
                    src={customWeather.blobUrl}
                    alt={customWeather.original_name || 'Custom GFA weather image'}
                    className="w-full h-full object-contain"
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                    data-testid="custom-weather-image"
                  />
                )}
                {isDragging && (
                  <div className="absolute inset-0 bg-orange-500 bg-opacity-30 flex items-center justify-center pointer-events-none">
                    <p className="text-white text-lg font-semibold">Drop to replace</p>
                  </div>
                )}
              </div>
              {(customWeather.uploaded_by || customWeather.uploaded_at) && (
                <p className={`mt-2 text-xs ${theme.colors.textMuted}`}>
                  {customWeather.original_name && <span className="font-semibold">{customWeather.original_name}</span>}
                  {customWeather.uploaded_by && <span> · uploaded by {customWeather.uploaded_by}</span>}
                  {customWeather.uploaded_at && <span> · {new Date(customWeather.uploaded_at).toLocaleString()}</span>}
                </p>
              )}
            </div>
          ) : errors.weather ? (
            <div
              className={`text-center py-4 flex-1 flex items-center justify-center ${isAdmin ? 'cursor-pointer' : ''}`}
              onClick={() => isAdmin && fileInputRef.current?.click()}
            >
              <div>
                <AlertCircle className={`h-8 w-8 ${theme.colors.textMuted} mx-auto mb-2`} />
                <p className={`text-sm ${theme.colors.textMuted}`}>{errors.weather}</p>
                {isAdmin && (
                  <p className="text-xs text-orange-500 mt-2">
                    Or drag &amp; drop a GFA image here to simulate a weather event
                  </p>
                )}
              </div>
            </div>
          ) : weatherImage ? (
            <div className="flex-1 flex flex-col min-h-0 relative">
              <div className="flex-1 bg-gray-900 rounded-lg overflow-hidden flex items-center justify-center min-h-0" style={{ maxHeight: 'calc(100% - 10px)' }}>
                <img 
                  src={weatherImage} 
                  alt={`Weather for ${weatherLocation.city}`}
                  className="w-full h-full object-contain"
                  style={{ maxWidth: '100%', maxHeight: '100%' }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23666">Weather Image</text></svg>';
                  }}
                />
              </div>
              {isAdmin && isDragging && (
                <div className="absolute inset-0 bg-orange-500 bg-opacity-30 flex items-center justify-center pointer-events-none rounded-lg">
                  <p className="text-white text-lg font-semibold">Drop GFA image to override</p>
                </div>
              )}
            </div>
          ) : (
            <div
              className={`text-center py-4 flex-1 flex items-center justify-center ${isAdmin ? 'cursor-pointer' : ''}`}
              onClick={() => isAdmin && fileInputRef.current?.click()}
            >
              <div>
                <Cloud className={`h-8 w-8 ${theme.colors.textMuted} mx-auto mb-2`} />
                <p className={`text-sm ${theme.colors.textMuted}`}>No weather data available</p>
                {isAdmin && (
                  <p className="text-xs text-orange-500 mt-2">
                    Drag &amp; drop a GFA image here or click &ldquo;Upload GFA&rdquo; to simulate a weather event
                  </p>
                )}
              </div>
            </div>
          )}
          {customWeatherError && (
            <p className="mt-2 text-xs text-red-500" data-testid="weather-error">{customWeatherError}</p>
          )}
        </div>

        {/* Right Column: Read-only Leaflet Map */}
        <div className={`${theme.colors.secondary} rounded-lg p-6 border ${theme.colors.border} flex flex-col`} style={{ minHeight: '500px' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <MapPin className={`h-5 w-5 ${theme.colors.textPrimary} mr-2`} />
              <h3 className={`text-lg font-semibold ${theme.colors.textPrimary}`}>Incident Area Map</h3>
            </div>
            <button
              onClick={() => {
                // Pass the current exercise ID via the URL so the display
                // mode always loads the correct exercise map (independent
                // of any stale value in localStorage).
                const url = exerciseId ? `#display-mapping?exercise=${exerciseId}` : '#display-mapping';
                window.open(url, '_blank');
              }}
              className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 flex items-center gap-1"
              title="Open in Display Mode"
            >
              <Monitor className="h-3 w-3" />
              Display Mode
            </button>
          </div>
          
          {errors.map ? (
            <div className="text-center py-4 flex-1 flex items-center justify-center">
              <div>
                <AlertCircle className={`h-8 w-8 ${theme.colors.textMuted} mx-auto mb-2`} />
                <p className={`text-sm ${theme.colors.textMuted}`}>{errors.map}</p>
              </div>
            </div>
          ) : loading ? (
            <div className="text-center py-4 flex-1 flex items-center justify-center">
              <div>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                <p className={`text-sm ${theme.colors.textMuted}`}>Loading map...</p>
              </div>
            </div>
          ) : mapCenter ? (
            <div className="flex-1 rounded" style={{ minHeight: '400px', position: 'relative' }} key={`dashboard-map-${exerciseId}-${mapObjects.length}`}>
              <MapErrorBoundary>
              <LeafletMapContainer
                key={`leaflet-${exerciseId}-${mapObjects.length}`}
                center={mapCenter}
                zoom={exerciseData?.zoom_level || 8}
                style={{ height: '100%', width: '100%', borderRadius: '8px', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
                zoomControl={true}
                dragging={true}
                touchZoom={true}
                doubleClickZoom={true}
                scrollWheelZoom={true}
                boxZoom={true}
                keyboard={true}
                zoomSnap={0.1}
                zoomDelta={0.5}
                zoomAnimation={false}
                fadeAnimation={false}
                markerZoomAnimation={false}
              >
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
                />
                
                {/* Render map objects as read-only */}
                {mapObjects.map((obj) => {
                  console.log('🗺️ Attempting to render object:', { type: obj.type, id: obj.id, name: obj.name, hasGeometry: !!obj.geometry, hasCoordinates: !!obj.geometry?.coordinates });
                  if (obj.type === 'marker' && obj.geometry?.coordinates) {
                    const [lng, lat] = obj.geometry.coordinates;
                    const iconType = obj.icon || 'default';
                    const iconData = ICS_ICONS[iconType] || ICS_ICONS['default'];
                    const iconColor = obj.color || '#3388ff';
                    
                    // Create custom DivIcon with colored pin and emoji
                    const customIcon = L.divIcon({
                      html: `<div style="
                        position: relative;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 40px;
                        height: 40px;
                      ">
                        <div style="
                          position: absolute;
                          bottom: 0;
                          left: 50%;
                          transform: translateX(-50%);
                          width: 32px;
                          height: 32px;
                          background-color: ${iconColor};
                          border-radius: 50% 50% 50% 0;
                          transform: translateX(-50%) rotate(-45deg);
                          border: 2px solid white;
                          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                        "></div>
                        <div style="
                          position: absolute;
                          bottom: 5px;
                          left: 50%;
                          transform: translateX(-50%);
                          font-size: 16px;
                          z-index: 1;
                        ">${iconData.emoji}</div>
                      </div>`,
                      className: 'custom-div-icon',
                      iconSize: [40, 40],
                      iconAnchor: [20, 40],
                      popupAnchor: [0, -40]
                    });
                    
                    return (
                      <Marker key={obj.id} position={[lat, lng]} icon={customIcon}>
                        <Popup>
                          <strong>{obj.name}</strong>
                          {obj.description && <p>{obj.description}</p>}
                        </Popup>
                      </Marker>
                    );
                  } else if (obj.type === 'polygon' && obj.geometry?.coordinates) {
                    const positions = obj.geometry.coordinates[0].map(coord => [coord[1], coord[0]]);
                    return (
                      <Polygon key={obj.id} positions={positions} pathOptions={{ color: obj.color || '#3388ff' }}>
                        <Popup>
                          <strong>{obj.name}</strong>
                          {obj.description && <p>{obj.description}</p>}
                        </Popup>
                      </Polygon>
                    );
                  } else if (obj.type === 'line' && obj.geometry?.coordinates) {
                    const positions = obj.geometry.coordinates.map(coord => [coord[1], coord[0]]);
                    return (
                      <Polyline key={obj.id} positions={positions} pathOptions={{ color: obj.color || '#3388ff' }}>
                        <Popup>
                          <strong>{obj.name}</strong>
                          {obj.description && <p>{obj.description}</p>}
                        </Popup>
                      </Polyline>
                    );
                  } else if (obj.type === 'rectangle' && obj.geometry?.coordinates) {
                    const positions = obj.geometry.coordinates[0].map(coord => [coord[1], coord[0]]);
                    return (
                      <Rectangle key={obj.id} bounds={[[positions[0][0], positions[0][1]], [positions[2][0], positions[2][1]]]} pathOptions={{ color: obj.color || '#3388ff' }}>
                        <Popup>
                          <strong>{obj.name}</strong>
                          {obj.description && <p>{obj.description}</p>}
                        </Popup>
                      </Rectangle>
                    );
                  } else if (obj.type === 'circle' && obj.geometry?.coordinates) {
                    const [lng, lat] = obj.geometry.coordinates;
                    const radius = obj.radius || 1000; // Default radius 1000 meters if not specified
                    
                    console.log('⭕ Rendering circle:', { id: obj.id, name: obj.name, lat, lng, radius, obj });
                    
                    // Validate coordinates and radius
                    if (!lat || !lng || isNaN(lat) || isNaN(lng) || !radius || isNaN(radius) || radius <= 0) {
                      console.warn('❌ Invalid circle data:', obj);
                      return null;
                    }
                    
                    return (
                      <Circle 
                        key={`circle-${obj.id}`} 
                        center={[parseFloat(lat), parseFloat(lng)]} 
                        radius={parseFloat(radius)}
                        pathOptions={{ color: obj.color || '#3388ff', fillColor: obj.color || '#3388ff', fillOpacity: 0.2 }}
                      >
                        <Popup>
                          <strong>{obj.name}</strong>
                          {obj.description && <p>{obj.description}</p>}
                          <p className="text-xs text-gray-600 mt-1">Radius: {Math.round(radius)}m</p>
                        </Popup>
                      </Circle>
                    );
                  }
                  return null;
                })}
              </LeafletMapContainer>
              </MapErrorBoundary>
            </div>
          ) : (
            <div className="text-center py-4 flex-1 flex items-center justify-center">
              <div>
                <MapPin className={`h-8 w-8 ${theme.colors.textMuted} mx-auto mb-2`} />
                <p className={`text-sm ${theme.colors.textMuted}`}>No map data available</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-6 text-center">
        <p className={`text-xs ${theme.colors.textMuted}`}>
          Weather card auto-refreshes every 2 hours.{' '}
          {weatherLastUpdated ? `Weather last updated: ${weatherLastUpdated.toLocaleString()}` : ''}
        </p>
      </div>
    </div>
  );
};

// Scenario Management Component

export default ICSDashboardOverview;
